/* eslint-disable no-redeclare */
import {
  Email,
  Gender,
  NonEmptyText,
  USAStateAcronym,
  ZipCode,
} from '@hc-frontend/core-types';
import { z } from 'zod';

import { QualifyingLifeEvent } from './update-prospect-dto';

export const UserAttributeCatalogKeyMember = z.enum([
  'Spouse',
  'Child_1',
  'Child_2',
  'Child_3',
  'Child_4',
]);

export type UserAttributeCatalogKeyMember = z.infer<
  typeof UserAttributeCatalogKeyMember
>;

export const JourneyAttributeCatalogKey = z.enum(['quality-life-event']);
export type JourneyAttributeCatalogKey = z.infer<
  typeof JourneyAttributeCatalogKey
>;

export const UserAttributeCatalogKey = z.enum([
  'address',
  'email',
  'zip',
  'state',
  'county',
  'city',
  'countyFips',
  'firstName',
  'lastName',
  'gender',
  'dateOfBirth',
  'phoneNumber',
  'maritalStatus',
  'householdSize',
  'householdIncome',
  'fpl',
  'tobaccoUser',
  'transUnionIncome',
  'Spouse-dateOfBirth',
  'Spouse-gender',
  'Spouse-tobacco',
  'Child_1-dateOfBirth',
  'Child_1-gender',
  'Child_1-tobacco',
  'Child_2-dateOfBirth',
  'Child_2-gender',
  'Child_2-tobacco',
  'Child_3-dateOfBirth',
  'Child_3-gender',
  'Child_3-tobacco',
  'Child_4-dateOfBirth',
  'Child_4-gender',
  'Child_4-tobacco',
]);

export type UserAttributeCatalogKey = z.infer<typeof UserAttributeCatalogKey>;

const IsoDateString = z.string(); //TODO validate date format

export const UserAttributeCatalog = {
  fpl: z.number(),
  address: NonEmptyText,
  email: Email,
  zip: ZipCode, // TODO: validate zip code
  state: USAStateAcronym,
  county: NonEmptyText,
  city: NonEmptyText,
  countyFips: z.string().nullable(),
  firstName: NonEmptyText,
  lastName: NonEmptyText,
  gender: Gender,
  dateOfBirth: IsoDateString,
  phoneNumber: z.string(), // TODO: validate phone number
  maritalStatus: z.enum(['SINGLE', 'MARRIED']),
  householdSize: z.number(),
  householdIncome: z.number(),
  tobaccoUser: z.boolean(),
  transUnionIncome: z.number().nullable(),
  'Spouse-gender': Gender,
  'Spouse-dateOfBirth': IsoDateString,
  'Spouse-tobacco': z.boolean(),
  'Child_1-dateOfBirth': IsoDateString,
  'Child_1-gender': Gender,
  'Child_1-tobacco': z.boolean(),
  'Child_2-dateOfBirth': IsoDateString,
  'Child_2-gender': Gender,
  'Child_2-tobacco': z.boolean(),
  'Child_3-dateOfBirth': IsoDateString,
  'Child_3-gender': Gender,
  'Child_3-tobacco': z.boolean(),
  'Child_4-dateOfBirth': IsoDateString,
  'Child_4-gender': Gender,
  'Child_4-tobacco': z.boolean(),
} satisfies Record<UserAttributeCatalogKey, z.ZodTypeAny>;

export const JourneyAttributeCatalog = {
  // There are more keys, but we are only using this one at the moment
  'quality-life-event': QualifyingLifeEvent,
} satisfies Record<JourneyAttributeCatalogKey, z.ZodTypeAny>;

export type JourneyAttributeCatalog = {
  [key in keyof typeof JourneyAttributeCatalog]: z.infer<
    (typeof JourneyAttributeCatalog)[key]
  >;
};

export const UserAttribute = z
  .object({
    type: z.enum(['question', 'calculated']),
    name: UserAttributeCatalogKey,
    value: z.string().or(z.number()).or(z.boolean()),
  })
  .refine(
    (val) => {
      const schema = UserAttributeCatalog[val.name];
      const result = schema.safeParse(val.value);
      return result.success;
    },
    (val) => ({
      message: `${val.name} has an incorrect type`,
    }),
  );

export const JourneyAttribute = z.object({
  name: NonEmptyText,
  value: z.string().nullable(),
});

export type UserAttributeCatalog = {
  [key in keyof typeof UserAttributeCatalog]: z.infer<
    (typeof UserAttributeCatalog)[key]
  >;
};

export type UserAttribute = z.infer<typeof UserAttribute>;

export type JourneyAttribute = z.infer<typeof JourneyAttribute>;

export enum GetExternalLeadResponseStatus {
  FRESH2 = 1,
  PURCHASE_COMPLETED = 2,
  PROSPECT = 3,
  SUBMITTED = 4,
  ENRICHED = 5,
  SCORED = 6,
  DELIVERED = 7,
  ROUTED = 8,
}

export const GetExternalLeadResponse = z.object({
  userId: z.string(),
  sessionId: z.string(),
  domainUserId: z.string().nullable(),
  domainSessionId: z.string().nullable(),
  src: z.string().nullable(),
  landingUrl: z.string().nullable(),
  trustedFormUrl: z.string().nullable(),
  tcpaUniversalId: z.string().nullable(),
  tcpaSession: z.string().nullable(),
  tcpaSite: z.string().nullable(),
  tcpaConsent: z.string().nullable(),
  tcpaUserAgent: z.string().nullable(),
  userAttributes: UserAttribute.array(),
  journeyAttributes: JourneyAttribute.array(),
  leadStatusId: z.nativeEnum(GetExternalLeadResponseStatus).nullable(),
});

export type GetExternalLeadResponse = z.infer<typeof GetExternalLeadResponse>;

export const GetExternalLeadRequest = z
  .object({
    sessionId: z.string(),
    userId: z.string(),
    domainUserId: z.string(),
    domainSessionId: z.string(),
    leadStatusId: z.string(),
  })
  .partial();

export type GetExternalLeadRequest = z.infer<typeof GetExternalLeadRequest>;
